import { OPTIVAL_CONSTANTS } from "../../../shared/Constants";
import type {
  RagAiEnhancement,
  RagAlternativeSource,
  RagPrimarySource,
  RagProcedure,
} from "../types/ragChatTypes";

export interface RagChatQueryRequest {
  query: string;
  sessionId?: string;
  isSuggestion?: boolean;
  isOptionSelection?: boolean;
  targetPdf?: string;
}

export interface RagChatQueryResponse {
  answer: string;
  responseType: string;
  sessionId: string;
  options: string[];
  images: Array<{ path: string; metadata?: { page?: string | number } }>;
  videos: Array<{
    path: string;
    metadata?: { pdf_name?: string; path?: string; type?: string };
  }>;
  sources: Array<{
    type?: string;
    path?: string;
    pdf_name?: string;
    page?: string | number;
  }>;
  inlineImages: Record<string, Array<{ path: string; metadata?: { page?: string | number } }>>;
  pageTables: Record<string, string[]>;
  pageAlerts: Record<string, Array<{ level: string; text: string }>>;
  relatedQuestions: string[];
  alternativePdfs: string[];
  // DF-1 / DF-2 — Ranked Source Presentation (camelCase top-level; nested keys
  // stay snake_case, mirroring `sources`/`images`).
  primarySource?: RagPrimarySource | null;
  alternativeSources?: RagAlternativeSource[];
  aiEnhancement?: RagAiEnhancement | null;
  refusalReason?: string | null;
  procedure?: RagProcedure | null;
}

const SESSION_KEY = "rag_session_id";

export const RAG_CHAT_API = {
  query: OPTIVAL_CONSTANTS.API_URLS.RAG_CHAT_QUERY,
  session: (id: string) => OPTIVAL_CONSTANTS.API_URLS.RAG_CHAT_SESSION(id),
};

export function getRagSessionId(): string {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID?.() ?? `session-${Date.now()}`;
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

export function resetRagSessionId(): string {
  const id = crypto.randomUUID?.() ?? `session-${Date.now()}`;
  sessionStorage.setItem(SESSION_KEY, id);
  return id;
}

/** JWT query param for browser tags (iframe/img/video) that cannot send Authorization. */
export function appendMediaAuthToken(url: string): string {
  const token = localStorage.getItem("jwtToken") || "";
  if (!token || !url) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}token=${encodeURIComponent(token)}`;
}

/** Map worker path (absolute, /media/..., or filename) to Instrakt-proxied URL. */
function mediaUrlFromWorkerPath(path: string): string | null {
  const trimmed = path.trim();
  if (!trimmed) return null;

  const normalized = trimmed.replace(/\\/g, "/");
  if (normalized.startsWith("/rag/")) {
    return normalized;
  }

  const lower = normalized.toLowerCase();
  const processedIdx = lower.indexOf("/processed/");
  if (processedIdx >= 0) {
    const rel = normalized.slice(processedIdx + "/processed/".length).replace(/^\/+/, "");
    return `/rag/media/${rel}`;
  }

  if (normalized.startsWith("/objects/")) {
    return `/rag${normalized}`;
  }
  if (normalized.startsWith("org_") && normalized.includes("/extracted/")) {
    const segments = normalized.split("/").map((s) => encodeURIComponent(s));
    return `/rag/objects/${segments.join("/")}`;
  }
  if (normalized.startsWith("/media/")) {
    return `/rag${normalized}`;
  }
  if (normalized.startsWith("/pdf/") || normalized.startsWith("/video/")) {
    return `/rag${normalized}`;
  }

  const imagesIdx = lower.indexOf("/images/");
  if (imagesIdx >= 0) {
    const rel = normalized.slice(imagesIdx + 1);
    return `/rag/media/${rel}`;
  }

  const fileName = normalized.split("/").pop() ?? "";
  if (/\.(png|jpe?g|gif|webp|svg)$/i.test(fileName)) {
    return `/rag/media/images/${fileName}`;
  }

  return null;
}

export function resolveMediaPath(path: string): string {
  if (!path) return "";
  if (path.startsWith("http")) {
    return path.includes("token=") ? path : appendMediaAuthToken(path);
  }

  const base = (process.env.REACT_APP_OPVA_API_BASE_URL || "").replace(/\/+$/, "");
  const mediaPath = mediaUrlFromWorkerPath(path);
  if (!mediaPath) return "";

  return appendMediaAuthToken(`${base}${mediaPath}`);
}

export function ragPdfPreviewUrl(pdfName: string, initialPage?: number): string {
  const base = (process.env.REACT_APP_OPVA_API_BASE_URL || "").replace(/\/+$/, "");
  const encoded = encodeURIComponent(pdfName);
  const page =
    initialPage != null && Number(initialPage) > 0 ? Number(initialPage) : 1;
  const fragment = page > 1 ? `#page=${page}` : "";
  return appendMediaAuthToken(`${base}/rag/pdf/${encoded}${fragment}`);
}

/** Raw PDF URL (no token, no fragment) for authenticated fetch. */
export function ragPdfUrl(pdfName: string): string {
  const base = (process.env.REACT_APP_OPVA_API_BASE_URL || "").replace(/\/+$/, "");
  const encoded = encodeURIComponent(pdfName);
  return `${base}/rag/pdf/${encoded}`;
}

/** Stream URL for video playback / frame preview (`<video>` needs `?token=`). */
export function ragVideoPreviewUrl(fileName: string): string {
  const base = (process.env.REACT_APP_OPVA_API_BASE_URL || "").replace(/\/+$/, "");
  const encoded = encodeURIComponent(fileName);
  return appendMediaAuthToken(`${base}/rag/video/${encoded}`);
}
